import { useMemo, useState } from 'react';

import { createColumnHelper, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';

import { UploadFailureReason } from '../../../../../../../../../../backend/src/types/types';
import { FAILURE_REASON_CONFIG } from '../../utils';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetUpload } from '@/api/queries/useGetUpload';
import { UploadFailure } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { downloadFailedFilesReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const columnHelper = createColumnHelper<UploadFailure>();

interface UploadDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  uploadId: string;
}

export function UploadDetailModal({ open, onOpenChange, matterId, uploadId }: UploadDetailModalProps) {
  const [globalFilter, setGlobalFilter] = useState('');
  const { data: upload, isLoading } = useGetUpload(matterId, uploadId);
  const { data: matter } = useGetMatterV2(matterId);

  const successCount = upload?.successfullyUploadedFileCount ?? 0;
  const failureCount = upload?.uploadFailures?.length ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
            <span className="text-sm text-gray-800">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('rejectedReason', {
        header: () => <span>Reason</span>,
        cell: (info) => {
          const reason = info.getValue();
          const config = reason ? FAILURE_REASON_CONFIG[reason as keyof typeof FAILURE_REASON_CONFIG] : null;
          if (!config) {
            return <span className="text-sm text-gray-500">{reason}</span>;
          }
          return (
            <div className="flex items-center gap-1.5">
              <Icon className={`${config.icon} h-3.5 w-3.5 text-amber-500`} />
              <span className="text-sm text-gray-700">{config.label}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor('duplicateFile', {
        header: () => <span>Duplicate Of</span>,
        cell: (info) => {
          const reason = info.row.original.rejectedReason;
          if (reason !== UploadFailureReason.Duplicate) {
            return <span className="text-sm text-gray-400">&mdash;</span>;
          }

          const duplicateFile = info.getValue();
          if (!duplicateFile) return <span className="text-sm text-gray-400">&mdash;</span>;

          return (
            <div className="flex items-center gap-2">
              {duplicateFile.doc?.id ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="h-auto px-0 py-0 font-medium text-blue-500"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (!matter?.allDocumentsCaseId) return;
                        window.open(
                          `/matters/${matterId}/subsets/${matter.allDocumentsCaseId}/docs/${duplicateFile.doc?.id}`,
                          '_blank',
                        );
                      }}
                    >
                      {duplicateFile.name.length > 12 ? `${duplicateFile.name.slice(0, 12)}...` : duplicateFile.name}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{duplicateFile.name}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm text-gray-600">{duplicateFile.name}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{duplicateFile.name}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Date</span>,
        cell: (info) => {
          const date = info.getValue();
          return <span className="text-sm text-gray-600">{date ? formatDate(date, true, true) : '—'}</span>;
        },
      }),
    ],
    [matter?.allDocumentsCaseId],
  );

  const table = useReactTable({
    data: upload?.uploadFailures ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] max-w-6xl flex-col">
        <DialogHeader>
          <DialogTitle>Upload Details</DialogTitle>
          <DialogDescription>
            {upload && (
              <span className="flex items-center gap-2">
                <Icon className="lucide--calendar h-3.5 w-3.5" />
                <span>{formatDate(upload.createdAt, true, true)}</span>
                {upload.createdBy?.email && (
                  <>
                    <span className="text-gray-300">|</span>
                    <Icon className="lucide--user h-3.5 w-3.5" />
                    <span>{upload.createdBy.email}</span>
                  </>
                )}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Stats */}
        <div className="mb-3 flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1.5 rounded-md bg-blue-50 px-2 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
            <span className="font-medium text-blue-900">{(successCount + failureCount).toLocaleString()}</span>
            <span className="text-blue-700">attempted</span>
          </div>
          <Icon className="lucide--arrow-right h-3 w-3 text-gray-300" />
          <div className="flex items-center overflow-hidden rounded-md">
            <div className="flex items-center gap-1.5 bg-green-50 px-2 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
              <span className="font-medium text-green-900">{successCount.toLocaleString()}</span>
              <span className="text-green-700">successfully uploaded</span>
            </div>
            <div className="flex items-center gap-1.5 bg-amber-50 px-2 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-600" />
              <span className="font-medium text-amber-900">{failureCount.toLocaleString()}</span>
              <span className="text-amber-700">skipped</span>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          {failureCount > 0 ? (
            <ScrollableTable
              table={table}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              plural="skipped files"
              loading={isLoading}
              className="h-full max-h-[50vh] px-1 pt-1"
              additionalButtons={
                <Button
                  variant="default"
                  size="sm"
                  className="ml-auto"
                  onClick={() => {
                    trackCustomEvent(TrackingEventsV2.FileHistoryDownloadUploadBatchSkippedReport, { matterId });
                    upload?.uploadFailures && downloadFailedFilesReport(upload.uploadFailures, uploadId);
                  }}
                  disabled={failureCount === 0}
                >
                  <Icon className="lucide--download h-3.5 w-3.5" />
                  Download Skipped Files Report
                </Button>
              }
            />
          ) : (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500">
              {isLoading ? (
                <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
              ) : (
                <div className="flex items-center gap-2">
                  <Icon className="lucide--check-circle size-3.5 text-green-500" />
                  <span className="text-sm text-gray-800">All files in this batch were uploaded successfully.</span>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
